import React, { useContext, useEffect, useMemo } from "react";
import {
  Collapse,
  Group,
  Flex,
  Box,
  Button
} from "@mantine/core";
import {IconCheck, IconFilter, IconX} from "@tabler/icons";
import { SplitButton } from "../../general/SplitButton";
import { showNotification } from "@mantine/notifications";
import SearchPanel from "./SearchPanel";
import { getConnectedUserRole } from "../../../utils";
import { useAuth0 } from "@auth0/auth0-react";
import FiltersContext from "../filters/context/FiltersContext";
import { useFiltersContextHelper } from "../filters/useFiltersContextHelper";
import SaveViewModal from "./SaveViewModal";
import { useEvidenceInclusion } from "../../../hooks/useEvidenceInclusion";
import {useFields} from "../../../hooks/useFields";
import {ActiveFilter as ReusableActiveFilter, AdditionalConfiguration} from "../../common/filters/types";
import ReusableFilterModal from "../../common/filters/ReusableFilterModal";
import { useIdeasUrlState } from "../../../hooks/useIdeasUrlState";
import {findFilterByName} from "../../../context/helpers";
import FilterBadge from "../filters/FilterBadge";
import {useIdeaFilters} from "../../../hooks/useIdeaFilters";
import {FILTERS_TO_HIDE} from "../../common/filters/utils";
import _ from "lodash";
import {editIdeaPage} from "../../../api/idea";
import { useIdeaPages } from "../../../hooks/useIdeaPages";
import { useCurrentUser } from "../../../hooks/useCurrentUser";

interface FilterActionsProps {
  activeFilters: any[];
  setActiveFilters: (filters: any[]) => void;
  selectedView: any;
  open: () => void;
  handleSaveViews: (data: any) => Promise<void>;
  opened: boolean;
  viewName: string;
  setViewName: (name: string) => void;
  doClose: () => void;
  toggle: () => void;
  search: string;
  setSearch: (search: string) => void;
  searchOpened: boolean;
  setSearchOpened: (opened: boolean) => void;
  actionIconsRef: React.MutableRefObject<any>
  isGlobal: boolean;
  setIsGlobal: (isGlobal: boolean) => void;
  isSubPageView: boolean;
  setIsSubPageView: (isSubPageView: boolean) => void;
  sortState: string;
  setSortState: (state: string) => void;
  filterModalOpened: boolean;
  setFilterModalOpened: (opened: boolean) => void;
  saveViewModalOpened: boolean;
  setSaveViewModalOpened: (opened: boolean) => void;
}

const isChangeLogFilter = (filter: any): boolean => {
  return filter?.dbFieldPath?.includes('.itemChangeLog[') &&
    filter?.operator?.match(/^\$(gt|gte|lt|lte):.+$/);
};

const FilterActions: React.FC<FilterActionsProps> = ({
  activeFilters,
  setActiveFilters,
  selectedView,
  open,
  handleSaveViews,
  opened,
  viewName,
  setViewName,
  doClose,
  toggle,
  search,
  setSearch,
  searchOpened,
  setSearchOpened,
  actionIconsRef,
  isGlobal,
  setIsGlobal,
  isSubPageView,
  setIsSubPageView,
  sortState,
  setSortState,
  filterModalOpened,
  setFilterModalOpened,
  saveViewModalOpened,
  setSaveViewModalOpened,
}) => {
  const { fields, setFields } = useFields();
  const { ideasFiltersConfig, loading: isLoadingFilters } = useIdeaFilters();
  const { subPageKey, editMode, setRangeDates, setOperator, setEditMode } = useContext(FiltersContext);
  const { setCheckedComponents } = useFiltersContextHelper();
  const { pages: subPages, invalidatePages } = useIdeaPages();

  const subPage = subPages?.find((subPage: any) => subPage.key === subPageKey);
  const auth0 = useAuth0();
  const currentUser = useCurrentUser();
  const role = getConnectedUserRole(currentUser);
  const canEdit = role === "admin";
  const { setOwner, setProductArea, setDateRange, setTabFilters } = useIdeasUrlState({ subPageKey });

  // Use evidence inclusion hook instead of local state
  const { assigned, suggested, setSuggested, evidenceInclusion } = useEvidenceInclusion();

  // Convert checkbox states to array format for backward compatibility
  const value = useMemo(() => {
    const result = [];
    if (assigned) result.push('assigned');
    if (suggested) result.push('suggested');
    return result;
  }, [assigned, suggested]);

  // Update sortState when evidence inclusion changes
  useEffect(() => {
    if (sortState !== evidenceInclusion) {
      setSortState(evidenceInclusion);
      if (process.env.NODE_ENV === 'development') {
        console.log('🎛️ Sort state changed:', { from: sortState, to: evidenceInclusion, checkboxes: value });
      }
    }
  }, [evidenceInclusion, sortState, setSortState, value]); 

  const handleOpenSaveViewModal = () => {
    setSaveViewModalOpened(true);
  };

  const handleCloseSaveViewModal = () => {
    setSaveViewModalOpened(false);
  };

  const handleSaveView = (data: any) => {
    try {
      handleSaveViews(data);
    } catch (error) {
      console.error('Failed to save view:', error);
    }
  };

  const handleFilterModalClose = () => {
    setFilterModalOpened(false);
  };

  // Handle removing a filter via badge click - includes URL state clearing for owner/product-area
  const handleRemoveFilter = (filterName: string) => {
    // Clear URL state and checkbox state for owner/product-area filters
    if (filterName === 'owner') {
      setOwner([]);
      // Clear checked components for owner
      setCheckedComponents((current: any) => {
        const next: { [key: string]: any[] } = {};
        Object.keys(current).forEach((key) => {
          next[key] = key.split(".")[0] === 'owner' ? [] : current[key];
        });
        return next;
      });
    } else if (filterName === 'product-area') {
      setProductArea([]);
      // Clear checked components for product-area
      setCheckedComponents((current: any) => {
        const next: { [key: string]: any[] } = {};
        Object.keys(current).forEach((key) => {
          next[key] = key.split(".")[0] === 'product-area' ? [] : current[key];
        });
        return next;
      });
    } else if (['created-at', 'createdAt', 'evidence_createdAt', 'evidenceCreatedAt'].includes(filterName)) {
      // Clear date filter state
      setRangeDates((prev: any) => ({
        ...(prev || {}),
        [filterName]: [null, null],
      }));
      setOperator((prev: any) => ({
        ...(prev || {}),
        [filterName]: undefined,
      }));
      setDateRange(null);
    }
    
    const next = activeFilters?.filter((item) => item?.name !== filterName) ?? [];
    setActiveFilters(next);
  };

  const additionalConfigurations: AdditionalConfiguration[] = [{
    id: 'evidenceInclusion',
    label: 'Show only verified evidence',
    type: 'switch',
    value: !suggested,
    onChange: (value: boolean) => setSuggested(!value)
  }]

  const currentTypeActiveFilters = activeFilters && activeFilters?.filter(filter => {
    // Always show default filters // For now show all filters
    if (_.includes(FILTERS_TO_HIDE, filter.name)) {
      return false;
    } 

    // Include change log filters that match the MenuType
    if (isChangeLogFilter(filter)) {
      return filter.filterObject === 'idea';
    }
    // Standard filter lookup
    return findFilterByName(filter.parent?.split(".").pop(), filter.filterObject, ideasFiltersConfig);
  })
    ?.map(filter => {
      // For change log filters, use the filter's own properties
      if (isChangeLogFilter(filter)) {
        return { ...filter, default: filter.default || false };
      }
      // Standard filter - merge with config
      return {
        ...filter,
        default: filter.default || false,
        ...findFilterByName(filter.parent?.split(".").pop(), filter.filterObject, ideasFiltersConfig)
      };
    })
    .filter(filter => {
      if (editMode) return true;
      return !filter.default;
    });


  return (
    <Collapse in={true}>
      <Group my={18.2}  position="apart"  noWrap align="center">
        <Flex>
          {/* <Group> REMOVED FILTER BADGES
            
            {currentTypeActiveFilters?.map((filter, index) => (
              <FilterBadge
                key={index}
                filter={filter}
                MenuType={filter.filterObject}
                editMode={editMode}
                // ownerOptions={[]}
                onRemoveClick={() => handleRemoveFilter(filter?.name)}
                selectedView={selectedView}
                onClick={() => setFilterModalOpened(true)}
                sx={{
                  marginRight: currentTypeActiveFilters.length == (index + 1) ? 4 : 0
                }}
              />
            ))}
          </Group> */}
          <Group spacing={4} noWrap mx={activeFilters?.length === 0 ? 0 : 'md'}>
            {/* <Box ref={actionIconsRef} mr={6}>
              <SearchPanel
                toggle={toggle}
                search={search}
                setSearch={setSearch}
                searchOpened={searchOpened}
                setSearchOpened={setSearchOpened}
              />
            </Box> */}
            { editMode && canEdit && 
            <SplitButton
              isSplit={(subPageKey && subPage?.defaultFilters?.length) ? true : (fields?.defaultFilters?.length && fields?.defaultFilters?.length > 0 ? true : false)}
              lable={"Save as page filter    "}
              sx= {{backgroundColor: "#fa5252"}}
              onClick={() => {
                // Create unique filters by name and add default: true
                const uniqueFiltersWithDefault = (activeFilters || []).reduce((acc: any[], filter: any) => {
                  const existingIndex = acc.findIndex((f: any) => f.name === filter.name);
                  if (existingIndex === -1) {
                    // Add new filter with default: true
                    acc.push({
                      ...filter,
                      default: true
                    });
                  } else {
                    // Update existing filter with default: true
                    acc[existingIndex] = {
                      ...filter,
                      default: true
                    };
                  }
                  return acc;
                }, []);

                editIdeaPage( auth0,
                  { filters: activeFilters || [], key: subPageKey })
                  .then((res) => {
                    // Update the specific subpage in subPages array
                    const updatedSubPages = fields?.subPages?.map((page: any) => 
                      page.key === subPageKey 
                        ? { ...page, defaultFilters: activeFilters || [] }
                        : page
                    );
                    if(fields){
                      setFields({...fields, subPages: updatedSubPages});
                    }
                    invalidatePages();
                    setActiveFilters(uniqueFiltersWithDefault);
                    setEditMode(false);
                    showNotification({
                      title: "Saved successfully!",
                      color: "teal",
                      message: "Filter saved successfully",
                      icon: <IconCheck size="1.1rem" />,
                      autoClose: 1200
                    });
                  })
                  .catch((err) => {
                    showNotification({
                      title: "Error",
                      color: "red",
                      message: "Error saving filter",
                    });
                  });
              }}
              items={[
                {
                  disabled: activeFilters?.length === 0,
                  id: "close",
                  onClick: () => {
                    editIdeaPage( auth0,
                      { filters: [], key: subPageKey })
                      .then((res) => {
                        console.log(res);
                        const nonDefaultFilters = (activeFilters || []).filter((filter: any) => !filter.default);
                        setActiveFilters(nonDefaultFilters);
                        setEditMode(false);
                        // Update the specific subpage in subPages array
                        const updatedSubPages = fields?.subPages?.map((page: any) => 
                          page.key === subPageKey 
                            ? { ...page, defaultFilters: [] }
                            : page
                        );
                        if(fields){
                          setFields({...fields, subPages: updatedSubPages});
                        }
                        invalidatePages();
                        showNotification({
                          title: "Default filters removed",
                          color: "blue",
                          message: "Default filters have been removed",
                          icon: <IconX size="1.1rem" />,
                          autoClose: 1200
                        });
                      })
                      .catch((err) => {
                        showNotification({
                          title: "Error",
                          color: "red",
                          message: "Error saving filter",
                        });
                      });
                  },
                  name: "Remove page filters",
                },
              ]}
              disabled={!activeFilters?.length}
            /> 
            }
            {/* {selectedView?.isGlobal &&  */}
            {/* {<SplitButton
              isSplit={false}
              lable={
                !opened
                  ? "Save as new view"
                  : !selectedView?.isGlobal
                    ? "Save view"
                    : "Save"
              }
              onClick={() => {
                console.log({
                  selectedView,
                  elseif: !selectedView?.isGlobal && !opened,
                });
                if (!opened) {
                  handleOpenSaveViewModal();
                }
              }}
              items={[
                {
                  id: "1",
                  onClick: () => handleOpenSaveViewModal(),
                  name: "Save as new view",
                },
              ]}
              disabled={!activeFilters?.length}
            />} */} 

            {/* Save View Modal */}
            <SaveViewModal
              opened={saveViewModalOpened}
              onClose={handleCloseSaveViewModal}
              onSave={handleSaveView}
              viewName={viewName}
              setViewName={setViewName}
              isGlobal={isGlobal}
              setIsGlobal={setIsGlobal}
              isSubPageView={isSubPageView}
              setIsSubPageView={setIsSubPageView}
              canEdit={canEdit}
              view={null}
            />



          </Group> 
        </Flex>

        <ReusableFilterModal
          opened={filterModalOpened}
          onClose={handleFilterModalClose}
          filtersConfig={ideasFiltersConfig}
          activeFilters={(activeFilters || []) as ReusableActiveFilter[]}
          setActiveFilters={(filters: ReusableActiveFilter[]) => {
            // Check if owner or product-area was removed and update URL state
            const prevOwner = activeFilters?.find((f: any) => f.name === 'owner');
            const prevProductArea = activeFilters?.find((f: any) => f.name === 'product-area');
            const newOwner = filters.find((f: any) => f.name === 'owner');
            const newProductArea = filters.find((f: any) => f.name === 'product-area');
            
            // If owner was removed or its selection was cleared
            if (prevOwner && !newOwner) {
              setOwner([]);
              // Clear checked components for owner
              setCheckedComponents((current: any) => {
                const next: { [key: string]: any[] } = {};
                Object.keys(current).forEach((key) => {
                  next[key] = key.split(".")[0] === 'owner' ? [] : current[key];
                });
                return next;
              });
            } else if (newOwner && (!newOwner.selected || (Array.isArray(newOwner.selected) && newOwner.selected.length === 0))) {
              setOwner([]);
              // Clear checked components for owner
              setCheckedComponents((current: any) => {
                const next: { [key: string]: any[] } = {};
                Object.keys(current).forEach((key) => {
                  next[key] = key.split(".")[0] === 'owner' ? [] : current[key];
                });
                return next;
              });
            }
            
            // If product-area was removed or its selection was cleared
            if (prevProductArea && !newProductArea) {
              setProductArea([]);
              // Clear checked components for product-area
              setCheckedComponents((current: any) => {
                const next: { [key: string]: any[] } = {};
                Object.keys(current).forEach((key) => {
                  next[key] = key.split(".")[0] === 'product-area' ? [] : current[key];
                });
                return next;
              });
            } else if (newProductArea && (!newProductArea.selected || (Array.isArray(newProductArea.selected) && newProductArea.selected.length === 0))) {
              setProductArea([]);
              // Clear checked components for product-area
              setCheckedComponents((current: any) => {
                const next: { [key: string]: any[] } = {};
                Object.keys(current).forEach((key) => {
                  next[key] = key.split(".")[0] === 'product-area' ? [] : current[key];
                });
                return next;
              });
            }

            // Sync date filter with globalFilters.dateRange
            // Only the EVIDENCE "Created At" date filter feeds the global date range.
            // Both conditions are required: filterObject must be 'evidence' (an
            // idea-level date filter, even one labeled "created", must never touch
            // this evidence-only global state), and the label/name must actually
            // reference "created" (other evidence date-type filters, e.g.
            // stateUpdatedAt, must not match here) — same "created" label/name
            // check as CreatedAtFiltersIdeas.tsx.
            const dateFilterNames = [/* 'created-at', 'createdAt', */ 'evidence_createdAt'];
            const isDateFilter = (f: any) => {
              if (f.type !== 'date' || f.filterObject !== 'evidence') return false;
              if (dateFilterNames.includes(f.name)) return true;
              const label = (f.label || f.name || '').toString().toLowerCase();
              return label.includes('created');
            };
            const prevDateFilter = activeFilters?.find(isDateFilter);
            const newDateFilter = filters.find(isDateFilter);
            
            // Helper to clear date filter state (same as selecting "All" in CreatedAtFiltersIdeas)
            const clearDateFilter = () => {
              // Get the createdAtKey from the previous filter - use parent or name
              const createdAtKey = prevDateFilter?.parent || prevDateFilter?.name || 'createdAt';
              setRangeDates((prev: any) => ({
                ...(prev || {}),
                [createdAtKey]: [null, null],
              }));
              setOperator((prev: any) => ({
                ...(prev || {}),
                [createdAtKey]: undefined,
              }));
              setDateRange(null);
            };
            
            // If date filter was removed or cleared
            if (prevDateFilter && !newDateFilter) {
              clearDateFilter();
            } else if (newDateFilter) {
              const selected = newDateFilter.selected;
              // Handle date filter format: selected can be [startDate, endDate] or { values: [start, end] }
              if (selected) {
                if (Array.isArray(selected) && selected.length === 2) {
                  setDateRange({ start: selected[0], end: selected[1] });
                } else if (typeof selected === 'object' && selected.values && Array.isArray(selected.values) && selected.values.length === 2) {
                  setDateRange({ start: selected.values[0], end: selected.values[1] });
                }
              } else {
                clearDateFilter();
              }
            }
            
            setActiveFilters(filters as any);
          }}
          filterObject="both"
          additionalConfigurations={ additionalConfigurations }
          selectedView={selectedView}
          isFromIdeas={true}
          onFiltersSaved={() => {
            // Optional: Add any additional logic after filters are saved
          }}
          onClearFilters={() => {
            // "Clear all" clears every active filter, including global ones —
            // owner/product-area/date-range always get reset here too.
            setTabFilters([]);
            setOwner([]);
            setProductArea([]);
            setDateRange(null);
            const dateFilterNames = ['created-at', 'createdAt', 'evidence_createdAt', 'evidenceCreatedAt'];
            // Clear date filter context state (same as selecting "All" in CreatedAtFiltersIdeas)
            dateFilterNames.forEach((key) => {
              setRangeDates((prev: any) => ({
                ...(prev || {}),
                [key]: [null, null],
              }));
              setOperator((prev: any) => ({
                ...(prev || {}),
                [key]: undefined,
              }));
            });
          }}
        />
      </Group>
    </Collapse>
  );
};

export default FilterActions;
